/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { IconProps } from '@coursera/cds-common';
import { Button, Dialog, Skeleton, TextField, Typography2 } from '@coursera/cds-core';
import {
  BriefcaseIcon,
  EyeIcon,
  FlagIcon,
  MaximizeIcon,
  MinimizeIcon,
  ReloadIcon,
  SmileIcon,
} from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import type {
  AIMessageDraft,
  MessageRewriteAction,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import MessageBodyEditor from 'bundles/enterprise-admin-messaging-v2/components/common/MessageBodyEditor';
import PersonalizationTagActions from 'bundles/enterprise-admin-messaging-v2/components/common/PersonalizationTagActions';
import { useMessagingTrackingData } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import useToneRewrite from 'bundles/enterprise-admin-messaging-v2/hooks/useToneRewrite';
import type { MessagingTextInputName } from 'bundles/enterprise-admin-messaging-v2/utils/eventing';
import {
  MESSAGING_BUTTON,
  MESSAGING_MODAL,
  MESSAGING_RADIO,
  MESSAGING_TEXT_INPUT,
} from 'bundles/enterprise-admin-messaging-v2/utils/eventing';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  draft: AIMessageDraft;
  updateDraft: (partial: Partial<AIMessageDraft>) => void;
  onSend: () => void;
  onBack: () => void;
  /**
   * Hook for the "View learner list" link. Stays enabled per design — falls
   * back to a no-op when the screen isn't wired up yet.
   */
  onViewLearnerList?: () => void;
  /**
   * Hook for the "Preview email" link. Stays enabled per design — falls
   * back to a no-op when the screen isn't wired up yet.
   */
  onPreviewEmail?: () => void;
  /**
   * True while the audience count is still resolving (filter/count APIs run in
   * parallel with generation). Shows a shimmer in place of the audience summary.
   */
  audienceLoading?: boolean;
  /** True while the message is being sent — drives the Send button spinner. */
  sending?: boolean;
  /** Error surfaced when sending fails; keeps the admin on Review to retry. */
  sendError?: string | null;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  field: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  audienceRow: css`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--cds-spacing-200);
  `,
  fieldLabel: css`
    color: var(--cds-color-neutral-primary);
  `,
  /* Wrapper that lays the action pills on the left and the Reset-original
     link on the right (visible only when a rewrite snapshot exists). */
  actionPillsRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--cds-spacing-100);
    flex-wrap: wrap;
  `,
  actionPills: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
  `,
  /* Pill-shaped trigger that fires a message-rewrite action. Per Figma:
     fully-rounded outline, 1px neutral border, dark text + icon, white bg. */
  actionPill: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-50);
    height: 36px;
    padding: 0 var(--cds-spacing-200);
    background: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 9999px;
    color: var(--cds-color-neutral-primary);
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;

    &:hover:not([aria-disabled='true']) {
      background: var(--cds-color-neutral-disabled-weak);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }

    &[aria-disabled='true'] {
      cursor: not-allowed;
      opacity: 0.6;
    }
  `,
  /* Selected state: weak blue background + blue stroke + blue text/icon.
     Applied while a rewrite using this action is the currently-active one. */
  actionPillSelected: css`
    background: var(--cds-color-emphasis-primary-background-weak);
    border-color: var(--cds-color-emphasis-primary-content-default);
    color: var(--cds-color-emphasis-primary-content-default);

    &:hover:not([aria-disabled='true']) {
      background: var(--cds-color-emphasis-primary-background-weak);
    }
  `,
  ctaRow: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--cds-spacing-200);
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
    width: 100%;
  `,
  previewLink: css`
    margin-left: auto;
  `,
  sendError: css`
    margin-bottom: var(--cds-spacing-100);
    color: var(--cds-color-feedback-error);

    /* Keep the alert region in the DOM for screen readers; collapse it when empty. */
    :empty {
      display: none;
    }
  `,
  /* Applied to the Reset-original button when no snapshot exists, so the
     button occupies its slot but is fully inert. Stable layout, no focus
     disruption when a rewrite first lands. */
  resetHidden: css`
    visibility: hidden;
    pointer-events: none;
  `,
};

type ActionConfig = {
  action: MessageRewriteAction;
  label: string;
  Icon: React.ComponentType<IconProps>;
};

const getActions = (): ActionConfig[] => [
  { action: 'shorten', label: _t('Shorten'), Icon: MinimizeIcon },
  { action: 'expand', label: _t('Expand'), Icon: MaximizeIcon },
  { action: 'more_casual', label: _t('More casual'), Icon: SmileIcon },
  { action: 'more_formal', label: _t('More formal'), Icon: BriefcaseIcon },
  { action: 'add_urgency', label: _t('Add urgency'), Icon: FlagIcon },
];

/**
 * Step 2 of the AI Message modal: the admin reviews the auto-generated
 * audience / subject / message, can run one of five rewrite actions on the
 * message body (Shorten / Expand / More casual / More formal / Add urgency),
 * and then sends. "View learner list" and "Preview email" stay clickable
 * per design intent; their handlers are stubbed until those screens ship.
 */
const ReviewAndSendScreen = ({
  draft,
  updateDraft,
  onSend,
  onBack,
  onViewLearnerList,
  onPreviewEmail,
  audienceLoading = false,
  sending = false,
  sendError = null,
}: Props) => {
  const actions = React.useMemo(() => getActions(), []);
  // Track whether the editor has emitted its initial mount/normalization change.
  // The first onChange from Slate is just normalization (whitespace, trailing
  // newlines) — we snapshot that as the baseline so subsequent real edits can be
  // detected by comparing against it at send time.
  const hasSnapshotted = React.useRef(false);
  const handleMessageChange = React.useCallback(
    (next: string) => {
      if (!hasSnapshotted.current) {
        hasSnapshotted.current = true;
        updateDraft({ message: next, originalAiMessage: next });
        return;
      }
      updateDraft({ message: next });
    },
    [updateDraft]
  );
  const { activeAction, isRewriting, hasSnapshot, triggerRewrite, resetOriginal } = useToneRewrite({
    message: draft.message,
    onMessageChange: handleMessageChange,
  });

  const track = useTracker();
  const trackingData = useMessagingTrackingData();

  // Manually-edited audience size: the filter flow adjusts the base cohort
  // count by the delta; the manual (CSV) flow uses the in-memory list length.
  // `addedIds` is reserved for a future add-from-pool flow and is always empty
  // today (the filter editor only supports removals), so it's a no-op for now.
  const effectiveSelectedCount = draft.audienceDelta
    ? draft.audienceCount - draft.audienceDelta.removedIds.length + draft.audienceDelta.addedIds.length
    : draft.learners.length;

  // Step impression.
  React.useEffect(() => {
    track('view_modal', { ...trackingData, modal: { name: MESSAGING_MODAL.CREATE_STEP2 } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Track a tone-pill selection, then run the existing rewrite.
   * @param action - The rewrite action backing the clicked pill.
   */
  const handleToneClick = React.useCallback<(action: MessageRewriteAction) => void>(
    (action) => {
      track('change_radio', {
        radio: { id: MESSAGING_RADIO.TONE_SELECTOR, value: action },
        ...trackingData,
        modal: { name: MESSAGING_MODAL.CREATE_STEP2 },
      });
      updateDraft({ messageTone: action });
      triggerRewrite(action);
    },
    [track, trackingData, triggerRewrite, updateDraft]
  );

  /** Track the "Reset original" click, then run the existing handler. */
  const handleResetOriginal = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.RESET_TO_ORIGINAL_MESSAGE },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.CREATE_STEP2 },
    });
    updateDraft({ messageTone: 'none' });
    resetOriginal();
  }, [track, trackingData, resetOriginal, updateDraft]);

  /** Fire a text-input focus event for one of the Review-and-send fields. */
  const trackTextInputFocus = React.useCallback<(name: MessagingTextInputName) => void>(
    (name) => {
      track('click_text_input', {
        textInput: { name },
        ...trackingData,
        modal: { name: MESSAGING_MODAL.CREATE_STEP2 },
      });
    },
    [track, trackingData]
  );

  /** Track "View learner list", then run the existing handler. */
  const handleViewLearnerList = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.VIEW_LEARNER_LIST },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.CREATE_STEP2 },
    });
    onViewLearnerList?.();
  }, [track, trackingData, onViewLearnerList]);

  /** Track "Preview email", then run the existing handler. */
  const handlePreviewEmail = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.PREVIEW_MESSAGE },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.CREATE_STEP2 },
    });
    onPreviewEmail?.();
  }, [track, trackingData, onPreviewEmail]);

  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          <div css={styles.audienceRow}>
            <div css={styles.field}>
              <Typography2 component="span" variant="subtitleMedium" css={styles.fieldLabel}>
                {_t('Audience')}
              </Typography2>
              <Typography2 component="span" variant="bodyPrimary" data-testid="ai-message-modal-audience-summary">
                {/* eslint-disable-next-line no-nested-ternary -- manual edit > loading shimmer > resolved summary */}
                {draft.isManuallyEdited ? (
                  <FormattedMessage
                    message={_t('{count, plural, one {# selected learner} other {# selected learners}}')}
                    count={effectiveSelectedCount}
                  />
                ) : audienceLoading ? (
                  <Skeleton width={200} data-testid="ai-message-modal-audience-loading" />
                ) : (
                  draft.audienceSummary
                )}
              </Typography2>
            </div>
            <Button
              variant="ghost"
              size="small"
              onClick={handleViewLearnerList}
              data-testid="ai-message-modal-view-learner-list"
            >
              {_t('View learner list')}
            </Button>
          </div>

          <div css={styles.field}>
            <Typography2 component="span" variant="subtitleMedium" css={styles.fieldLabel}>
              {_t('Subject')}
            </Typography2>
            <TextField
              aria-label={_t('Subject')}
              value={draft.subject}
              onChange={(event) => updateDraft({ subject: event.target.value })}
              onFocus={() => trackTextInputFocus(MESSAGING_TEXT_INPUT.SUBJECT)}
              data-testid="ai-message-modal-subject"
            />
          </div>

          <div css={styles.field}>
            <Typography2 component="span" variant="subtitleMedium" css={styles.fieldLabel}>
              {_t('Message')}
            </Typography2>
            <div css={styles.actionPillsRow}>
              <div css={styles.actionPills}>
                {actions.map(({ action, label, Icon }) => {
                  const isSelected = activeAction === action;
                  return (
                    <button
                      key={action}
                      type="button"
                      css={[styles.actionPill, isSelected && styles.actionPillSelected]}
                      // Row 12: use aria-disabled (not the `disabled` attribute) while a
                      // rewrite is in flight. A focused button that becomes `disabled`
                      // hands focus to the next field — the Call-to-action box — which is
                      // the cursor-jump bug. Keeping it focusable holds focus on the pill;
                      // the guard below still blocks re-clicks during the rewrite.
                      onClick={() => {
                        if (!isRewriting) {
                          handleToneClick(action);
                        }
                      }}
                      aria-disabled={isRewriting}
                      aria-pressed={isSelected}
                      data-testid={`ai-message-modal-action-${action}`}
                    >
                      <Icon size="small" />
                      {label}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="ghost"
                size="small"
                onClick={handleResetOriginal}
                icon={<ReloadIcon />}
                iconPosition="before"
                disabled={isRewriting || !hasSnapshot}
                aria-hidden={!hasSnapshot}
                css={!hasSnapshot ? styles.resetHidden : undefined}
                data-testid="ai-message-modal-reset-original"
              >
                {_t('Reset original')}
              </Button>
            </div>
            <MessageBodyEditor
              value={draft.message}
              onChange={handleMessageChange}
              renderToolbarActions={(insertText) => (
                <PersonalizationTagActions onInsert={insertText} showEnrollmentTag={draft.isEnrollmentSpecific} />
              )}
              maxHeight={280}
              disabled={isRewriting}
              disabledLabel={_t('Rewriting…')}
              aria-label={_t('Message body')}
              data-testid="ai-message-modal-message-body"
              onFocus={() => trackTextInputFocus(MESSAGING_TEXT_INPUT.BODY)}
            />
          </div>

          <div css={styles.ctaRow}>
            <TextField
              label={_t('Call to action')}
              value={draft.ctaText}
              onChange={(event) => updateDraft({ ctaText: event.target.value })}
              onFocus={() => trackTextInputFocus(MESSAGING_TEXT_INPUT.CTA_LABEL)}
              data-testid="ai-message-modal-cta-text"
            />
            <TextField
              label={_t('Button URL')}
              value={draft.ctaUrl}
              onChange={(event) => updateDraft({ ctaUrl: event.target.value })}
              onFocus={() => trackTextInputFocus(MESSAGING_TEXT_INPUT.BUTTON_URL)}
              type="url"
              autoComplete="url"
              inputProps={{ inputMode: 'url' }}
              data-testid="ai-message-modal-cta-url"
            />
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Typography2 component="p" variant="bodySecondary" role="alert" css={styles.sendError}>
          {sendError}
        </Typography2>
        <div css={styles.actions}>
          <Button variant="primary" onClick={onSend} loading={sending} data-testid="ai-message-modal-send">
            {_t('Send')}
          </Button>
          <Button variant="ghost" onClick={onBack} data-testid="ai-message-modal-back">
            {_t('Back')}
          </Button>
          <Button
            variant="ghost"
            onClick={handlePreviewEmail}
            icon={<EyeIcon />}
            iconPosition="before"
            css={styles.previewLink}
            data-testid="ai-message-modal-preview-email"
          >
            {_t('Preview')}
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default ReviewAndSendScreen;
